import React, { createContext, ReactNode, useContext, useState } from 'react';
import { Session } from './AuthContext';
import { useDebugger } from './DebuggerContext';

// Event enum for standardizing event names
export enum ApplicationEvent {
  // Listing events
  ListingCreate = 'Listing Create',
  ListingPublish = 'Listing Publish',

  // Global events
  PageView = 'Page View',
  FormError = 'Form Error',
}

interface AnalyticsContextType {
  isInitialized: boolean;
  isLoading: boolean;
  error: Error | null;
  initializeAnalytics: (session: Session) => Promise<void>;
  captureEvent: (
    eventName: Event | string,
    properties?: Record<string, any>,
  ) => void;
}

export const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const debug = useDebugger();

  const createPendoIdentity = (session: Session) => {
    if (!session?.user?.agent) return null;

    const agent = session.user.agent;

    return {
      visitor: {
        id: agent.MemberMlsId,
        name: `${agent.MemberPreferredFirstName} ${agent.MemberPreferredLastName}`,
        email: agent.MemberEmail,
        role: agent.MemberSubType,
        association: agent.MemberAssociationPrimary,
        county: agent.MemberCounty,
      },
      account: {
        id: agent.OfficeBrokerMlsId,
        name: agent.OfficeBrokerMlsId,
      },
    };
  };

  const initializeAnalytics = async (session: Session) => {
    if (isInitialized) return;

    try {
      setIsLoading(true);
      debug.log('Analytics', 'Initializing analytics services');

      // Initialize Pendo
      if ((window as any).pendo) {
        const identity = createPendoIdentity(session);

        if (identity) {
          (window as any).pendo.initialize(identity);
          debug.log('Analytics', 'Pendo analytics initialized', 'debug');
        } else {
          debug.log(
            'Analytics',
            'Could not initialize Pendo: missing identity information',
            'warn',
          );
        }
      } else {
        debug.log('Analytics', 'Pendo not available in window object', 'debug');
      }

      setIsInitialized(true);
      debug.groupEnd();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to initialize analytics';
      debug.log(
        'Analytics',
        `Error initializing analytics: ${errorMessage}`,
        'error',
      );
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to initialize analytics'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const captureEvent = (
    eventName: Event | string,
    properties?: Record<string, any>,
  ) => {
    if (!isInitialized) {
      debug.log(
        'Analytics',
        `Analytics not initialized, event tracking skipped: ${eventName}`,
        'warn',
      );
      return;
    }

    try {
      debug.log('Analytics', `Tracking event: ${eventName}`, 'debug');

      // Track with Pendo
      if ((window as any).pendo) {
        (window as any).pendo.track(eventName, properties);
      } else {
        debug.log(
          'Analytics',
          'Pendo not available, event tracking skipped',
          'debug',
        );
      }

      // Here you can add additional analytics platforms in the future
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      debug.log(
        'Analytics',
        `Error tracking event ${eventName}: ${errorMessage}`,
        'error',
      );
    }
  };

  return (
    <AnalyticsContext.Provider
      value={{
        isInitialized,
        isLoading,
        error,
        initializeAnalytics,
        captureEvent,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
